import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import ApplicationContext from '../../../ApplicationContext';
import { ApiResult, InsertResultsWithData } from '../../../framework.core/data/ApiResult';
import { getTbNameByJobName, JobNames } from '../../../framework.core/JobNames';
import useDirtyCheck from '../../../hooks/useDirtyCheck2';

import HblMilestoneView from './implementation/HblMilestone';

import { useTypedFastStorage } from '../../../hooks/useFastStorage';

import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import { TrackableEntity } from '../../../fms.core/data/ChangeTrackable';
import { checkPropDirty, getSQLInfoList2 } from '../../../fms.core/data/TrackingModule';
import { HblStatusViewModel } from '../../../fms.core/HblStatusViewModel';
import SecondaryButton from '../../../ui/SecondaryButton';
import { stringsEqualIgnoreCase } from '../../../util';
import { SQLField, SQLInfo } from '../../../framework.core/data/SQLInfo';
import { historyUpdate } from '../../../fms.data/modules/FmsModFunc';
import { BlEventLog } from '../../../fms.core/BlEventLog';
import { BlEventName } from '../../../framework.core/BlEventName';
import { getFmsGlobal } from '../../../globals';
import constants from '../../../constants';

function getStorageKey(id: string) {
  return `oi-hbl-bl-status-${id}`;
}


function HblMilestoneImpl({
  hblId,
  storageKey,
  onClose,
  isView,
}: {
  hblId: number,
  storageKey: string,
  onClose: (s: HblStatusViewModel|null) => void,
  isView: boolean,
}) {
  const appContext = useContext(ApplicationContext);

  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [initialized, setInitialized] = useState(false);

  const [blStatus, setBlStatus, removeBlStatus, blStatusRef] = useTypedFastStorage<HblStatusViewModel>(getStorageKey(hblId.toString()), null);
  const {setCleanState, /*isDirtyComparedTo,*/ dirtyCleanup} = useDirtyCheck(getStorageKey(hblId.toString()), ['updated', 'updatedBy']);

  useEffect(() => {
      initialize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function doClose() {
    dirtyCleanup();
    cleanup();
    onClose(null);
  }

  function cleanup() {
    removeBlStatus();
  }

  async function loadBlStatus() {
    const apiResult:ApiResult<HblStatusViewModel>= await appContext.api.getHblStatusViewModel(JobNames.OEHbl, hblId);

    if (!apiResult) {
      await showGlobalOkDialog('Cannot load data');
      onClose(null);
      return;
    }

    const loadedModelObj = new HblStatusViewModel(apiResult.result);
    setCleanState(loadedModelObj);

    setBlStatus(new HblStatusViewModel(_.cloneDeep(loadedModelObj)));

    if(blStatusRef.current !== null) {
      blStatusRef.current.trackable = true;
    }

    setInitialized(true);
  }

  async function initialize() {
    await loadBlStatus();
  }

  async function save() {
    if (!blStatus || !blStatusRef.current) {
      throw new Error('Invalid operation');
    }

    if(!blStatusRef.current.isDirty) {
      //await showGlobalOkDialog(<p>Nothing to save!!!</p>);
      doClose();
      return;
    }

    const si:SQLInfo[] = [];

    let hiSqlInfo = historyUpdate(getTbNameByJobName(JobNames.OEHbl), hblId, appContext.user?.id??'', 'Update B/L Info');
    if (hiSqlInfo) {
      si.push(hiSqlInfo);
    }
    
    const sqlInfoList = getSQLInfoList2(blStatusRef.current as TrackableEntity, HblStatusViewModel.getFieldMappings(JobNames.OEHbl));
    sqlInfoList.push(...si);

    sqlInfoList.filter(x => stringsEqualIgnoreCase(x.tableName, "t_aotherinfo")).forEach(x => {
      SQLInfo.setWhereFieldValueByFieldName(x, 'f_tbid', hblId);
    });

    sqlInfoList.filter(x => stringsEqualIgnoreCase(x.tableName, "t_oohmain")).forEach(x => {
      if(!SQLInfo.hasWhereFieldOfPropertyWithValue(x, 'f_id', hblId)){
        x.whereFields.push(new SQLField({fieldName: 'f_id', value: hblId}));
      }
    });

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveHblStatusViewModel(sqlInfoList);

    if (apiResult && apiResult.success) {
      await UpdateLogHblMilestone();    //[t49-webhook] LogMblMilestone. 이 기능은 TrackingService를 사용하지 않더라도 적용한다.
      await showGlobalOkDialog(<p>The data has been saved</p>);
      onClose(blStatusRef.current);
      
    } else {
      await showGlobalOkDialog(<div>
        <p>Cannot save</p>
        <p>{apiResult?.message}</p>
      </div>);
    }
  }

  async function UpdateLogHblMilestone() {
    if (!blStatus) return;

    let cmile:BlEventLog[] = [];

    if (checkPropDirty(blStatus, 'f3461Date')) {
      cmile.push(new BlEventLog(BlEventName.Fms3461, blStatus.f3461Date))
    }
    if (checkPropDirty(blStatus, 'f7501Date')) {
      cmile.push(new BlEventLog(BlEventName.Fms7501, blStatus.f7501Date))
    }
    if (checkPropDirty(blStatus, 'doSentDate')) {
      cmile.push(new BlEventLog(BlEventName.FmsDoSent, blStatus.doSentDate))
    }
    if (checkPropDirty(blStatus, 'delSchedDate')) {
      cmile.push(new BlEventLog(BlEventName.FmsDelSched, blStatus.delSchedDate))
    }
    if (checkPropDirty(blStatus, 'delActDate')) {
      cmile.push(new BlEventLog(BlEventName.FmsDelAct, blStatus.delActDate))
    }

    if (cmile.length > 0) {
      const tempData = cmile.sort((a, b) => new Date(a.eventAt ?? '').getTime() - new Date(b.eventAt ?? '').getTime());
      const lastEvents: BlEventLog = tempData[tempData.length - 1];
      lastEvents.suntekID = getFmsGlobal().AcmeID ?? '';
      lastEvents.jobName = JobNames.OEHbl;
      lastEvents.jobId = hblId;
      lastEvents.blNo = blStatus.hblNo;
      //lastEvents.eventAtUtc
      lastEvents.u1Id = appContext.user?.id ?? '';
      const apiResult = await appContext.api.trackingLogHblMilestone(lastEvents);;
      if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
      }
    }
  }

  return (
    <div>
      {!initialized &&
      <div style={{position: 'relative', top: '-25px'}}>
        <SkeletonTheme color="#4287f5" highlightColor="#b9d2fa">
          <Skeleton count={1} height={2} />
        </SkeletonTheme>
      </div>
      }
      {!!blStatus &&
        <div className='report-popup'>
          <HblMilestoneInitializedImpl blStatus={blStatus} setBlStatus={setBlStatus} isView={isView} />

          <div className='report-footer'>
            <SecondaryButton
              size="regular"
              style={{width: '90px'}}
              onClick={save}>
              Update
            </SecondaryButton>
            <SecondaryButton
              size="regular"
              style={{width: '90px'}}
              onClick={onClose}>
              Cancel
            </SecondaryButton>
          </div>
        </div>
      }
    </div>
  );
}

function HblMilestoneInitializedImpl({
  blStatus,
  setBlStatus,
  isView,
}: {
  blStatus: HblStatusViewModel,
  setBlStatus: (x:Partial<HblStatusViewModel>) => void,
  isView: boolean,
}) {
  const appContext = useContext(ApplicationContext);

  /*const containerInfo:OhContainer[] = hbl.container ?? [];

  function changeContainerInfo(ci:OhContainer[]) {
    setHbl({ container: ci });
  }*/

  return (
    <HblMilestoneView
      blStatus={blStatus}
      onChange={setBlStatus}
      isView={isView}
      /*containerInfo={containerInfo}
      onContainerInfoChange={changeContainerInfo}*/
      user={appContext.user?.id ?? ''}
    />
  );
}

const HblMilestonePopup = ({
  hblId,
  storageKey,
  onClose,
  isView,
}:{
  hblId: number,
  storageKey: string,
  onClose: (s: HblStatusViewModel|null) => void,
  isView: boolean,
}) => {
  return (
    <HblMilestoneImpl hblId={hblId} key={storageKey} storageKey={storageKey} onClose={onClose} isView={isView} />
  );
};

HblMilestonePopup.defaultProps = {
};

HblMilestonePopup.propTypes = {
  hblId: PropTypes.number.isRequired,
  storageKey: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  isView: PropTypes.bool.isRequired,
};

export default React.memo(HblMilestonePopup);
